import React, { useEffect, useRef } from 'react';

import { useRecaptcha } from '@fd/customHooks/useRecaptcha';
import Grid from '@material-ui/core/Grid';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { Form, FormikProps, withFormik } from 'formik';
import { withSnackbar, withSnackbarProps } from 'notistack';
import ReCAPTCHA from 'react-google-recaptcha';
import { getActiveLanguage, getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompose';

import { accountActions } from '../../../actions/account.actions';
import { accountConstants } from '../../../constants/account.constants';
import { maxLength, validate } from '../../../helpers/validation';
import { accountRoutesConst as routes } from '../../../routes/account.routes';
import InfoIcon from '../../../ui/Tooltip/InfoIcon';
import Tooltip from '../../../ui/Tooltip/Tooltip';
import CookiePreferences from '../Common/CookiePreferences';
import { IntroButton } from '../Common/IntroButton';
import AuthLayout from '../Common/Layout';
import LoginTitle from '../Login/LoginTitle';
import AcceptLegal from './AcceptLegal';
import UnderlinedTextButton from './UnderlinedTextButton';

type FormValues = {
  email: string;
  storeName: string;
  RecaptchaToken?: string;
};

const useStyles = makeStyles((theme: Theme) => ({
  formFields: {
    // remove chrome autofill color
    '& input:-webkit-autofill': {
      boxShadow: '0 0 0 30px white inset !important',
    },
  },
  textField: {
    // prevent jumping screen
    minHeight: '80px',
  },
  link: {
    textDecoration: 'none',
    textTransform: 'uppercase',
  },
  infoIcon: {
    position: 'relative',
    right: '-8px',
    top: '16px',
  },
  recaptchaContainer: {
    marginBottom: theme.spacing(3),
  },
  bottomContainer: {
    marginTop: 'auto',
    display: 'flex',
  },
}));

const Title = () => (
  <Translate>
    {(translate) => (
      <LoginTitle title={translate('Your_store_efficiency_is_about_to_shoot_up') as string}>
        <Typography variant="subtitle1" style={{ color: '#ffffff', letterSpacing: 'normal' }}>
          <Translate id="Easy_setup" />
          <br />
          <Translate id="Cancel_at_any_time" />
        </Typography>
      </LoginTitle>
    )}
  </Translate>
);

const BasicInformation = ({
  errors,
  isValid,
  isSubmitting,
  handleBlur,
  handleChange,
  handleSubmit,
  language,
  touched,
  translate,
  values,
  setFieldValue,
}: Props) => {
  const classes = useStyles();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const {
    isRecaptchaValid,
    recaptchaSiteKey,
    handleRecaptchaTest,
    handleRecaptchaExpired,
  } = useRecaptcha();

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (isFormValid) {
      handleSubmit();
    }
  };

  useEffect(() => {
    setFieldValue('RecaptchaToken', recaptchaRef.current?.getValue());
  }, [isRecaptchaValid]);

  const isFormValid = isValid && !isSubmitting && isRecaptchaValid;

  return (
    <AuthLayout title={Title}>
      <Grid container alignItems="stretch" direction="column" wrap="nowrap" spacing={2}>
        <Grid item>
          <Form onSubmit={handleFormSubmit}>
            <Grid container spacing={0} className={classes.formFields}>
              <Grid item xs={12} lg={11}>
                <TextField
                  name="email"
                  label={translate('Email')}
                  placeholder={translate('Email') as string}
                  type="email"
                  autoFocus
                  autoComplete="email"
                  value={values.email}
                  fullWidth
                  inputProps={{
                    maxLength: maxLength.email,
                    'data-fd': 'email',
                  }}
                  className={classes.textField}
                  error={touched.email && !!errors.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.email && errors.email}
                />
              </Grid>
              <Grid item xs={11} lg={11}>
                <TextField
                  name="storeName"
                  label={translate('Store_name')}
                  placeholder={translate('Store_name') as string}
                  autoComplete="off"
                  fullWidth
                  inputProps={{
                    'data-fd': 'store-name',
                    maxLength: maxLength.storeName,
                  }}
                  className={classes.textField}
                  error={touched.storeName && !!errors.storeName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.storeName && errors.storeName}
                  aria-describedby="Sign_up_store_name_tooltip"
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip
                  messageId={'Sign_up_store_name_tooltip'}
                  fdKey="Sign_up_store_name_tooltip"
                  id="Sign_up_store_name_tooltip"
                >
                  <div className={classes.infoIcon}>
                    <InfoIcon size={20} />
                  </div>
                </Tooltip>
              </Grid>
              <Grid className={classes.recaptchaContainer} item>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  data-fd="signup-recaptcha"
                  hl={language}
                  sitekey={recaptchaSiteKey}
                  onChange={handleRecaptchaTest}
                  onExpired={handleRecaptchaExpired}
                />
              </Grid>
              <Grid item xs={12} lg={11}>
                <IntroButton
                  resource={accountConstants.LOGIN}
                  loading={isSubmitting}
                  disabled={!isFormValid}
                  data-fd="create-your-store"
                >
                  <Translate id="Create_your_store" />
                </IntroButton>
              </Grid>
            </Grid>
          </Form>
        </Grid>
        <Grid item container direction="column">
          <Grid item xs={12}>
            <AcceptLegal translate={translate} />
          </Grid>
        </Grid>
        <Grid item container direction="column">
          <Grid item>
            <UnderlinedTextButton fdKey="login-instead" to={routes.Login}>
              <Translate id="Login_instead" />
            </UnderlinedTextButton>
          </Grid>
        </Grid>
        <Grid item className={classes.bottomContainer}>
          <CookiePreferences />
        </Grid>
      </Grid>
    </AuthLayout>
  );
};

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = () => {
  return (state: AppState) => {
    const { account } = state;
    return {
      account,
      translate: getTranslate(state.locale),
      language: getActiveLanguage(state.locale),
      Cid: state.account.Cid,
      Rid: state.account.Rid,
    };
  };
};

type Props = MappedState &
  withSnackbarProps & { dispatch: ThunkDispatch } & FormikProps<FormValues>;
export default compose<Props, {}>(
  setDisplayName('Signup'),
  withSnackbar,
  connect(mapStateToPropsFactory),
  withFormik<Props, FormValues>({
    displayName: 'SignupForm',
    mapPropsToValues: (props) => {
      return {
        email: props.account.Email || '',
        storeName: '',
      };
    },
    handleSubmit: async (values, { props, setSubmitting, setFieldError }) => {
      const { dispatch } = props;
      try {
        await dispatch(
          accountActions.create({
            ...values,
            languageCode: props.language,
            Rid: props.account.Rid,
            Cid: props.account.Cid,
          })
        );
        await dispatch(accountActions.getAccountDetails());
      } catch (error) {
        if (
          error.message === 'User_already_exists_with_pass' ||
          error.message === 'User_already_exists_with_no_pass'
        ) {
          setFieldError('email', props.translate('Email_address_in_use') as string);
          props.enqueueSnackbar(props.translate('Email_address_in_use'), {
            variant: 'error',
          });
        }
        setSubmitting(false);
      }
    },
    validate: (values) => {
      let errors;
      const email =
        values.email.length === 0 ? validate.required(values.email) : validate.email(values.email);
      if (email) {
        errors = { email };
      }
      const storeName = validate.required(values.storeName);
      if (storeName) {
        errors = { ...errors, storeName };
      }
      return errors;
    },
  })
)(BasicInformation);
